import React from 'react';
import axios from 'axios';
import ReactSwitch from 'react-switch';
import moment from 'moment/moment';
import I18n from 'i18n-js';
import useGradesTableStore from '@/components/staffer/AssignmentsBasedGradesTable/store/useGradesTableStore';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import { COLORS } from '@/components/ui/ui.constants';
import { plato_api_staffer_path } from '@/modules/routes';
import { GradeSyncEventsByPair } from '@/components/staffer/AssignmentsBasedGradesTable/types';

export const formatSyncTimestamp = (timestamp: string | undefined) => {
  if (!timestamp) return 'N/A';

  const localTime = moment(timestamp).local();
  const timezone = moment.tz(moment.tz.guess()).zoneAbbr();

  if (I18n.locale === 'es') {
    return `${localTime.format('D [de] MMMM [de] YYYY [a las] h:mmA')} (${timezone})`;
  }

  return `${localTime.format('MMM DD, YYYY [at] h:mmA')} (${timezone})`;
};

// PLAT-18052: last_success_at is already the newest 2xx per pair, so this only has to pick the
// newest across pairs — the previous version filtered the latest event by http_status, which meant a
// pair whose most recent attempt failed stopped contributing even though it had synced fine earlier.
export const getLastSyncedAt = (events: GradeSyncEventsByPair) => {
  let latest: string | undefined;

  Object.values(events).forEach((byStudent) => {
    Object.values(byStudent).forEach((event) => {
      const syncedAt = event?.last_success_at;
      if (!syncedAt) return;
      if (!latest || new Date(syncedAt).getTime() > new Date(latest).getTime()) {
        latest = syncedAt;
      }
    });
  });

  return latest;
};

const LMSSyncStatusToolbar = () => {
  const classroom = useGradesTableStore(state => state.classroom);
  const setStore = useGradesTableStore(state => state.setStore);
  const showLMSSyncStatus = useGradesTableStore(state => state.showLMSSyncStatus);
  const stafferId = useGradesTableStore(state => state.stafferId);
  const gradeSyncEvents = useGradesTableStore(state => state.gradeSyncEvents);
  const lastSyncedAt = getLastSyncedAt(gradeSyncEvents);

  if (!classroom.connected_lms_type) return null;

  const handleToggle = () => {
    const newValue = !showLMSSyncStatus;
    setStore({ showLMSSyncStatus: newValue });
    axios.patch(plato_api_staffer_path(stafferId), { show_lms_sync_status: newValue })
      .catch(() => setStore({ showLMSSyncStatus: !newValue }));
  };

  return (
    <div className="tw-flex tw-items-center tw-ml-[12.75rem] tw-mt-2 tw-justify-between tw-gap-4">
      <div className="tw-flex tw-items-center tw-ml-4 tw-gap-4 tw-shrink-0">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Icon className="tw-shrink-0" fill={COLORS.green} icon="CloudSuccess" size={20} />
          <Text size="sm">{I18n.t('synced_to_lms')}</Text>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <Icon className="tw-shrink-0" fill={COLORS['dark-grey']} icon="CloudPending" size={20} />
          <Text size="sm">{I18n.t('pending_sync')}</Text>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <Icon className="tw-shrink-0" fill={COLORS.red} icon="CloudFailed" size={20} />
          <Text size="sm">{I18n.t('sync_failed')}</Text>
        </div>
      </div>
      <div className="tw-flex tw-items-center tw-gap-4">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Text size="sm">{I18n.t('show_sync_status')}</Text>
          <ReactSwitch
            checked={showLMSSyncStatus}
            checkedIcon={false}
            handleDiameter={14}
            height={18}
            id="show-sync-status-switch"
            offColor={COLORS['med-grey']}
            uncheckedIcon={false}
            width={36}
            onChange={handleToggle}
            onColor={COLORS['tci-purple']}
          />
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <Icon className="tw-shrink-0" fill={COLORS.green} icon="CloudSuccess" size={20} />
          <Text size="sm">
            {`${I18n.t('last_synced_on')}: `}
            {formatSyncTimestamp(lastSyncedAt)}
          </Text>
        </div>
      </div>
    </div>
  );
};

export default LMSSyncStatusToolbar;
